import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Stack,
  Title,
  Datagrid,
  Text,
  Button,
  IconButton
} from '@efast_public/fjd-component-library';
import { useQuery } from '@tanstack/react-query';
import { GovosApiRoutes } from '../api/govos-api';
import {
  MarketplaceOdResponse,
  StartAppsResponse,
  OnlinedienstTemplate
} from '../api/govos.types';
import { debounce, DEFAULT_DEBOUNCE_DELAY } from '../utils/debounce';
import { AddOnlinedienstFromMarketplaceModal } from '../modals/AddOnlinedienstFromMarketplaceModal';
import { MarketplaceOdInformationModal } from '../modals/MarketplaceOdInformationModal';

const TABLE_HEADER = 44;
const ITEM_HEIGHT = 66;
const PAGE_SIZE = 10;
export function MarketplaceOnlinedienstPage() {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState<{
    template: OnlinedienstTemplate;
    modal: 'add' | 'info';
  } | null>(null);
  const [filteredTemplates, setFilteredTemplates] = useState<
    OnlinedienstTemplate[] | null
  >(null);

  const { data: startApps } = useQuery<StartAppsResponse>({
    queryKey: [GovosApiRoutes.startApps()]
  });

  // Eigentlich sollten wir hier keine startappId verwenden
  const { data: onlinedienste, isLoading } = useQuery<MarketplaceOdResponse>({
    queryKey: [GovosApiRoutes.marketplaceOnlinedienste()],
    enabled: (startApps?.userInfo.startApps.length ?? 0) > 0
  });

  const handleSearchChange = debounce((filter: string) => {
    setFilteredTemplates(
      onlinedienste?.onlineServices.filter((template) => {
        const search = filter.toLowerCase();
        return (
          template.title.toLowerCase().includes(search) ||
          template.ozg?.id?.toLowerCase().includes(search) ||
          template.fimId?.toLowerCase().includes(search)
        );
      }) ?? []
    );
  }, DEFAULT_DEBOUNCE_DELAY);

  useEffect(() => {
    if (onlinedienste) {
      setFilteredTemplates(onlinedienste.onlineServices);
    }
  }, [onlinedienste]);

  return (
    <Stack spacing="xxl" orientation="vertical">
      <Title level="2">{t('OnlinedienstPage.title')}</Title>

      <Input
        placeholder={t('OnlinedienstPage.searchBarPlaceholder')}
        onChange={(e) => handleSearchChange(e.target.value)}
        style={{ width: '100%' }}
      />

      <Datagrid<OnlinedienstTemplate>
        columns={[
          {
            id: 'title',
            header: t('AddOnlinedienstModal.columns.title'),
            accessorKey: 'title',
            enableHiding: false,
            size: 300,
            minSize: 600,
            enableSorting: true,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => (
              <Stack spacing="xxs" style={{ maxWidth: '50vw' }}>
                <Text strong size="l" ellipsis>
                  {row.row.original.title}
                </Text>
                <Text
                  variant="secondary"
                  size="s"
                  ellipsis
                >{`OZG-ID: ${row.row.original.ozg?.id || '-'} | FIM-ID: ${row.row.original.fimId || '-'} | LeiKa-ID's: ${row.row.original.leika?.join(',') || '-'} `}</Text>
              </Stack>
            )
          },
          {
            id: 'zustaendigeStelle',
            header: t('OnlinedienstPage.columns.zustaendigeStelle'),
            accessorKey: 'accessType',
            enableHiding: false,
            size: 170,
            minSize: 300,
            enableSorting: true,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => {
              const isPrivate = row.row.original.accessType === 'private';
              const allowedZS = row.row.original.allowedZS as
                | number[]
                | undefined;
              const hasAllowedZS = allowedZS && allowedZS.length > 0;

              return (
                <Text>
                  {isPrivate && hasAllowedZS
                    ? t(
                        'OnlinedienstPage.columns.zustaendigeStelle.eingeschraenkt'
                      )
                    : t('OnlinedienstPage.columns.zustaendigeStelle.alle')}
                </Text>
              );
            }
          },
          {
            id: 'menu',
            header: '',
            accessorKey: 'menu',
            enableHiding: false,
            size: 100,
            minSize: 80,
            enableSorting: false,
            enableResizing: false,
            sortingFn: 'auto',
            cell: (row) => (
              <Stack direction="row" spacing="xs">
                <IconButton
                  icon="information-outline"
                  size="sm"
                  variant="secondary-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTemplate({
                      template: row.row.original,
                      modal: 'info'
                    });
                  }}
                  label={t('AddOnlinedienstModal.button.info')}
                />
                <IconButton
                  icon="view"
                  size="sm"
                  variant="secondary-outline"
                  disabled={!row.row.original.previewLink}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(row.row.original.previewLink, '_blank');
                  }}
                  label={t('AddOnlinedienstModal.button.add')}
                />
                <Button
                  size="sm"
                  beforeIcon="add-outline"
                  variant="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedTemplate({
                      template: row.row.original,
                      modal: 'add'
                    });
                  }}
                >
                  {t('AddOnlinedienstModal.button.add')}
                </Button>
              </Stack>
            )
          }
        ]}
        isDataLoading={isLoading}
        data={filteredTemplates ?? []}
        labels={{
          currentPage: t('AddOnlinedienstModal.labels.currentPage'),
          detailButton: t('AddOnlinedienstModal.labels.detailButton'),
          emptyData: t('AddOnlinedienstModal.labels.emptyData'),
          nextPage: t('AddOnlinedienstModal.labels.nextPage'),
          of: t('AddOnlinedienstModal.labels.of'),
          page: t('AddOnlinedienstModal.labels.page'),
          pageSize: t('AddOnlinedienstModal.labels.pageSize'),
          previousPage: t('AddOnlinedienstModal.labels.previousPage'),
          rows: t('AddOnlinedienstModal.labels.rows'),
          tableTitle: '',
          ascBtn: t('AddOnlinedienstModal.labels.ascBtn'),
          descBtn: t('AddOnlinedienstModal.labels.descBtn'),
          hideBtn: t('AddOnlinedienstModal.labels.hideBtn')
        }}
        manualPagination={false}
        dataKey="id"
        manualSorting={false}
        areColumnsResizeable
        tableHeight={PAGE_SIZE * ITEM_HEIGHT + TABLE_HEADER}
        paginationState={{ pageSize: PAGE_SIZE, pageIndex: 0 }}
        multiSelect={false}
        totalDataCount={filteredTemplates?.length}
      />
      {selectedTemplate && selectedTemplate.modal === 'add' && (
        <AddOnlinedienstFromMarketplaceModal
          isOpen
          onClose={() => setSelectedTemplate(null)}
          selectedService={selectedTemplate.template}
        />
      )}
      {selectedTemplate && selectedTemplate.modal === 'info' && (
        <MarketplaceOdInformationModal
          isOpen
          template={selectedTemplate.template}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </Stack>
  );
}
